import { useMutation, useQueryClient } from 'react-query'
import {
  CreateProjectParams,
  Folder,
  Project,
  useFSTrackEvent,
} from '@dc-shared-web/hooks'
import { CrunchData } from '@dc-shared-web/hooks/api/document/types'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import {
  createProject,
  createProjectOld,
} from '@dc-shared-web/hooks/api/project/createProject'
import { FS } from '@dc-shared-web/lib/trackingConstants'
import {
  useAxiosContext,
  useSvcCrunchesContext,
} from '@dc-shared-web/providers'

export const useCreateProject = (params?: {
  onSuccess?: () => void
  onError?: () => void
}) => {
  const apiClient = useSvcCrunchesContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { trackEvent } = useFSTrackEvent()
  return useMutation(
    (mutationParams: CreateProjectParams) =>
      createProject(apiClient, mutationParams),
    {
      onSuccess: async (_, context) => {
        trackEvent(FS.PROJECT_CREATED)

        await Promise.allSettled([
          queryClient.invalidateQueries<Project[]>(`team-projects`),
          ...(context.crunchUUID
            ? [
                queryClient.invalidateQueries<CrunchData[]>({
                  queryKey: ['Document', context.crunchUUID],
                }),
              ]
            : []),
          queryClient.invalidateQueries<Project[]>(['projects']),
          queryClient.invalidateQueries<Folder[]>(['folders', 'list']),
          queryClient.invalidateQueries<Folder>(['folder']),
          queryClient.invalidateQueries<Project>(['project']),
        ])

        toast({
          variant: 'success',
          title: `${context.name} project has been added.`,
        })

        params?.onSuccess?.()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error creating project',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}

// This createProjectOld is required until the new one (/api/project-info/) is updated to receive a crunchUUID so that when creating a project form the infoPanel, the new project gets assigned to the open crunch
export const useCreateProjectOld = (params?: {
  onSuccess?: () => void
  onError?: () => void
}) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { trackEvent } = useFSTrackEvent()

  return useMutation(
    (mutationParams: CreateProjectParams) =>
      createProjectOld(apiClient, mutationParams),
    {
      onSuccess: async (_, context) => {
        trackEvent(FS.PROJECT_CREATED)

        await Promise.allSettled([
          queryClient.invalidateQueries<Project[]>(`team-projects`),
          ...(context.crunchUUID
            ? [
                queryClient.invalidateQueries<CrunchData[]>({
                  queryKey: ['Document', context.crunchUUID],
                }),
              ]
            : []),
          queryClient.invalidateQueries<Project[]>(['projects']),
          queryClient.invalidateQueries<Folder[]>(['folders', 'list']),
        ])

        toast({
          variant: 'success',
          title: `${context.name} project has been added.`,
        })

        params?.onSuccess?.()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error creating project',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
